import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  BaseChatState,
  BotAgentStage,
  CartPreview,
  CheckoutReady,
  ProductDetailsPayload,
  ProductImagesPayload,
  SuggestedReply,
  UIMessage,
} from '../types/chat.types';
import { appendSegmentBreak } from '../utils/appendSegmentBreak';

/** Sideband bundle attached to bot:done, dispatched in one FLUSH_MESSAGE action. */
export interface ChatTurnSideband {
  searchResults?: { tool: string; results: any[]; items?: any[] };
  productDetails?: ProductDetailsPayload;
  productImages?: ProductImagesPayload;
  cartPreview?: CartPreview;
  checkoutReady?: CheckoutReady;
  suggestedReplies?: SuggestedReply[];
  currentStage?: BotAgentStage;
}

export type ChatAction =
  | { type: 'ADD_MESSAGE'; message: UIMessage }
  | { type: 'SET_MESSAGES'; messages: UIMessage[] }
  | { type: 'APPEND_CHUNK'; text: string }
  | { type: 'MARK_SEGMENT_BREAK' }
  | { type: 'FLUSH_MESSAGE'; sideband?: ChatTurnSideband }
  | { type: 'SET_TOOL_LABEL'; label: string | null }
  | { type: 'SET_SOCKET_STATUS'; status: BaseChatState['socketStatus'] }
  | { type: 'SET_OPEN'; isOpen: boolean }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR_SUGGESTED_REPLIES' }
  | { type: 'SET_CURRENT_STAGE'; stage: BotAgentStage | null }
  | {
      type: 'SET_LIST_SELECTION';
      messageId: string;
      selection: { key: string | number; label: string };
    }
  | { type: 'MARK_LAST_SUPERSEDED' }
  /** Undo the optimistic user bubble when the send never left the client (e.g.
   *  session creation was rejected), handing the text back to the composer so
   *  the user doesn't lose what they typed. No-op unless the last message is
   *  actually a user message, so it can't eat a bot reply. */
  | { type: 'REVERT_PENDING_SEND' }
  /** ChatInput acknowledges it has taken `pendingInputRestore` back. */
  | { type: 'CONSUME_INPUT_RESTORE' }
  /** Start (`until` = epoch ms) or clear (`null`) the per-session send
   *  cooldown the BE hands us on a rate-limited turn. */
  | { type: 'SET_SEND_COOLDOWN'; until: number | null }
  | { type: 'RESET' };

const initialState: BaseChatState = {
  messages: [],
  socketStatus: 'disconnected',
  streamingText: '',
  isStreaming: false,
  activeToolLabel: null,
  isOpen: false,
  isLoadingHistory: false,
  suggestedReplies: [],
  currentStage: null,
  supersededMsgIds: [],
  pendingInputRestore: null,
  sendCooldownUntil: null,
};

function chatReducer(state: BaseChatState, action: ChatAction): BaseChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      // A new user message replaces any stale suggested-reply chips and
      // signals that this turn's sideband (cart_preview, etc.) is now history.
      return {
        ...state,
        messages: [...state.messages, action.message],
        suggestedReplies: action.message.role === 'user' ? [] : state.suggestedReplies,
      };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'APPEND_CHUNK':
      return { ...state, isStreaming: true, streamingText: state.streamingText + action.text };
    // Dispatched on `bot:searching`: a tool call ends one segment of the turn
    // and starts another. See appendSegmentBreak for why they used to fuse.
    case 'MARK_SEGMENT_BREAK':
      return {
        ...state,
        streamingText: appendSegmentBreak(state.streamingText),
      };
    case 'FLUSH_MESSAGE': {
      const sb = action.sideband ?? {};
      const nextSuggested = sb.suggestedReplies ?? state.suggestedReplies;
      const nextStage = sb.currentStage ?? state.currentStage;
      // Normally an empty-text turn produces no bubble. But a checkout-ready
      // turn must still surface a message so its checkoutReady sideband (and the
      // in-text checkout link rendered from it) is never dropped — even on the
      // rare turn where the LLM emitted no closing prose.
      if (!state.streamingText && !sb.checkoutReady?.ready) {
        return {
          ...state,
          isStreaming: false,
          streamingText: '',
          activeToolLabel: null,
          suggestedReplies: nextSuggested,
          currentStage: nextStage,
        };
      }
      const msg: UIMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: state.streamingText,
        timestamp: new Date().toISOString(),
        searchResults: sb.searchResults,
        productDetails: sb.productDetails,
        productImages: sb.productImages,
        cartPreview: sb.cartPreview,
        checkoutReady: sb.checkoutReady,
      };
      return {
        ...state,
        messages: [...state.messages, msg],
        streamingText: '',
        isStreaming: false,
        activeToolLabel: null,
        suggestedReplies: nextSuggested,
        currentStage: nextStage,
      };
    }
    case 'SET_TOOL_LABEL':
      return { ...state, activeToolLabel: action.label };
    case 'SET_SOCKET_STATUS':
      return { ...state, socketStatus: action.status };
    case 'SET_OPEN':
      return { ...state, isOpen: action.isOpen };
    case 'SET_LOADING':
      return { ...state, isLoadingHistory: action.loading };
    case 'CLEAR_SUGGESTED_REPLIES':
      return { ...state, suggestedReplies: [] };
    case 'SET_CURRENT_STAGE':
      return { ...state, currentStage: action.stage };
    case 'SET_LIST_SELECTION':
      // Record which option the user picked on a given turn's option list, so
      // that once the list is superseded it can collapse to a marked header.
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, listSelection: action.selection }
            : m,
        ),
      };
    case 'MARK_LAST_SUPERSEDED': {
      // Latch the current last message as superseded the moment a new turn
      // starts. Latching (instead of deriving from isStreaming) keeps its
      // option lists in the collapsed view-only state permanently — no
      // flicker back to interactive when a turn flushes with empty text.
      // `?? []` guards state shapes created before this field existed
      // (HMR-preserved state, persisted snapshots).
      const superseded = state.supersededMsgIds ?? [];
      const lastId = state.messages[state.messages.length - 1]?.id;
      if (!lastId || superseded.includes(lastId)) return state;
      return {
        ...state,
        supersededMsgIds: [...superseded, lastId],
      };
    }
    case 'REVERT_PENDING_SEND': {
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'user') return state;
      return {
        ...state,
        messages: state.messages.slice(0, -1),
        // Drop the revert from the superseded set too, so re-sending the same
        // text doesn't leave a dangling id behind.
        supersededMsgIds: (state.supersededMsgIds ?? []).filter((id) => id !== last.id),
        pendingInputRestore: last.text ?? null,
      };
    }
    case 'CONSUME_INPUT_RESTORE':
      if (state.pendingInputRestore === null) return state;
      return { ...state, pendingInputRestore: null };
    case 'SET_SEND_COOLDOWN':
      if (state.sendCooldownUntil === action.until) return state;
      return { ...state, sendCooldownUntil: action.until };
    case 'RESET':
      // Preserve `isOpen` so tab switches (which RESET chat state) don't
      // close the drawer. Same for `socketStatus` — the active connection
      // can keep flowing; the BotWidget useEffect re-binds on sessionId
      // change.
      return {
        ...initialState,
        isOpen: state.isOpen,
        socketStatus: state.socketStatus,
      };
    default:
      return state;
  }
}

interface ChatContextValue {
  state: BaseChatState;
  dispatch: React.Dispatch<ChatAction>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  return <ChatContext.Provider value={{ state, dispatch }}>{children}</ChatContext.Provider>;
};

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be inside ChatProvider');
  return ctx;
}
